/**
 * Usage/debug event reporter. App code calls logUsage(kind, detail) at coarse
 * milestones (sign-in, section navigation, project open/save, exports);
 * global error handlers add 'client-error' events. Events queue locally and
 * flush in batches — every 30s, and on tab hide/close via keepalive fetch
 * with the last-known token (token acquisition is async, so unload-time
 * flushes reuse the newest one a timed flush obtained).
 *
 * Inert unless auth is enabled (identity comes from the server-validated
 * token; there is no anonymous logging).
 */

import { authDevBypass, authEnabled, getApiToken } from './msal';

interface QueuedEvent {
  kind: string;
  at: number;
  detail?: Record<string, unknown>;
}

const FLUSH_INTERVAL_MS = 30_000;
const MAX_QUEUE = 200;

const sessionId = Math.random().toString(36).slice(2, 10);
let queue: QueuedEvent[] = [];
let lastToken: string | null = null;
let started = false;

/** Truncate one detail value so a single event can't bloat the log. */
function clampDetail(detail: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!detail) return undefined;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(detail)) {
    out[k] = typeof v === 'string' && v.length > 500 ? `${v.slice(0, 500)}…` : v;
  }
  return out;
}

export function logUsage(kind: string, detail?: Record<string, unknown>): void {
  if (!authEnabled) return;
  ensureStarted();
  if (queue.length >= MAX_QUEUE) queue.shift();
  queue.push({ kind, at: Date.now(), detail: clampDetail(detail) });
}

function buildHeaders(token: string | null): HeadersInit {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (authDevBypass) headers['x-dev-user'] = 'dev-user';
  else if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function flush(): Promise<void> {
  if (queue.length === 0) return;
  const batch = queue.splice(0, 50);
  try {
    if (!authDevBypass) lastToken = await getApiToken();
    const res = await fetch('/api/events', {
      method: 'POST',
      headers: buildHeaders(lastToken),
      body: JSON.stringify({ sessionId, events: batch }),
    });
    if (!res.ok) throw new Error(String(res.status));
  } catch {
    // Put the batch back (front) and retry on the next tick — offline-safe.
    queue = [...batch, ...queue].slice(0, MAX_QUEUE);
  }
}

/** Fire-and-forget flush for pagehide — keepalive, no token refresh. */
function flushOnHide(): void {
  if (queue.length === 0) return;
  if (!authDevBypass && !lastToken) return; // nothing usable; events drop
  const batch = queue.splice(0, 50);
  void fetch('/api/events', {
    method: 'POST',
    keepalive: true,
    headers: buildHeaders(lastToken),
    body: JSON.stringify({ sessionId, events: batch }),
  }).catch(() => undefined);
}

function ensureStarted(): void {
  if (started) return;
  started = true;
  window.setInterval(() => void flush(), FLUSH_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushOnHide();
  });
  window.addEventListener('pagehide', flushOnHide);

  // Debugging trail: uncaught errors and promise rejections.
  window.addEventListener('error', (ev) => {
    logUsage('client-error', {
      message: String(ev.message ?? ''),
      source: `${ev.filename ?? ''}:${ev.lineno ?? ''}`,
      stack: ev.error instanceof Error ? (ev.error.stack ?? '') : '',
    });
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev.reason;
    logUsage('client-error', {
      message: reason instanceof Error ? reason.message : String(reason),
      stack: reason instanceof Error ? (reason.stack ?? '') : '',
      unhandledRejection: true,
    });
  });
}
